/**
 * Keeps a New Job in progress on this device, so a closed modal, a frozen
 * phone or a killed PWA does not cost the mechanic what they had typed.
 *
 * Two things are kept, per organisation:
 *
 *   1. The DRAFT — the New Job form before "Create Job" is tapped. Nothing
 *      exists in the database yet, so this is the only copy. Cleared the
 *      moment the job is created.
 *   2. The RESUME pointer — the id of a job created from the New Job flow and
 *      not yet invoiced. The job itself is already saved (every part and
 *      labour line is written as it is added); this only lets the dashboard
 *      offer "Resume job" instead of making the user hunt for it in Workshop.
 *
 * Deliberately local, not a server-side draft job: a draft row would count
 * against the free-tier job limit and leave abandoned jobs behind.
 *
 * The draft holds customer contact details, so it expires after a day and is
 * wiped on sign-out (App.tsx). It is keyed by organisation so switching orgs
 * never shows one business another's customer.
 */

const DRAFT_PREFIX = 'fd_new_job_draft:';
const RESUME_PREFIX = 'fd_new_job_resume:';

export const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
export const RESUME_TTL_MS = 3 * 24 * 60 * 60 * 1000;

export interface JobDraftForm {
    customerName: string;
    customerPhone: string;
    customerEmail: string;
    vrm: string;
    make: string;
    model: string;
    colour: string;
    mileage: string;
    year: string;
    engine: string;
    fuelType: string;
    vehicleSource?: 'dvla' | 'db' | 'manual';
    jobDescription: string;
}

export interface JobDraftCustomer {
    id: string;
    full_name: string | null;
    phone: string | null;
    email: string | null;
}

export interface JobDraft {
    form: JobDraftForm;
    linkedCustomer: JobDraftCustomer | null;
    vehicleConfirmed: boolean;
    savedAt: number;
}

export interface JobResume {
    jobId: string;
    /** What the banner shows: the reg and the customer, as typed. */
    label: string;
    savedAt: number;
}

const FORM_KEYS: (keyof JobDraftForm)[] = [
    'customerName', 'customerPhone', 'customerEmail', 'vrm', 'make', 'model',
    'colour', 'mileage', 'year', 'engine', 'fuelType', 'jobDescription',
];

/** True when the form holds something worth keeping. */
export function isDraftWorthKeeping(form: JobDraftForm): boolean {
    return FORM_KEYS.some((k) => typeof form[k] === 'string' && (form[k] as string).trim() !== '');
}

function read<T>(key: string): T | null {
    try {
        const raw = localStorage.getItem(key);
        return raw ? (JSON.parse(raw) as T) : null;
    } catch {
        remove(key);
        return null;
    }
}

function write(key: string, value: unknown): void {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // Private mode or quota: the flow still works, it just is not kept.
    }
}

function remove(key: string): void {
    try {
        localStorage.removeItem(key);
    } catch {
        // Nothing to do.
    }
}

export function saveJobDraft(orgId: string, draft: Omit<JobDraft, 'savedAt'>, now: number = Date.now()): void {
    if (!isDraftWorthKeeping(draft.form)) {
        clearJobDraft(orgId);
        return;
    }
    write(DRAFT_PREFIX + orgId, { ...draft, savedAt: now });
}

/** The saved draft, or null if absent, malformed or older than a day. */
export function readJobDraft(orgId: string, now: number = Date.now()): JobDraft | null {
    const draft = read<Partial<JobDraft>>(DRAFT_PREFIX + orgId);
    if (!draft) return null;
    const form = draft.form;
    const valid = typeof draft.savedAt === 'number'
        && !!form && typeof form === 'object'
        && FORM_KEYS.every((k) => typeof form[k] === 'string');
    if (!valid || now - (draft.savedAt as number) > DRAFT_TTL_MS) {
        clearJobDraft(orgId);
        return null;
    }
    return {
        form: form as JobDraftForm,
        linkedCustomer: draft.linkedCustomer && typeof draft.linkedCustomer.id === 'string' ? draft.linkedCustomer : null,
        vehicleConfirmed: draft.vehicleConfirmed === true,
        savedAt: draft.savedAt as number,
    };
}

export function clearJobDraft(orgId: string): void {
    remove(DRAFT_PREFIX + orgId);
}

export function saveJobResume(orgId: string, jobId: string, label: string, now: number = Date.now()): void {
    write(RESUME_PREFIX + orgId, { jobId, label, savedAt: now });
}

/** The job to offer for resuming, or null if absent, malformed or stale. */
export function readJobResume(orgId: string, now: number = Date.now()): JobResume | null {
    const resume = read<Partial<JobResume>>(RESUME_PREFIX + orgId);
    if (!resume) return null;
    if (typeof resume.jobId !== 'string' || !resume.jobId
        || typeof resume.savedAt !== 'number'
        || now - resume.savedAt > RESUME_TTL_MS) {
        clearJobResume(orgId);
        return null;
    }
    return { jobId: resume.jobId, label: typeof resume.label === 'string' ? resume.label : '', savedAt: resume.savedAt };
}

export function clearJobResume(orgId: string): void {
    remove(RESUME_PREFIX + orgId);
}

/** Wipe every draft and resume pointer on this device — called on sign-out. */
export function clearAllJobDrafts(): void {
    try {
        const doomed: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.startsWith(DRAFT_PREFIX) || key.startsWith(RESUME_PREFIX))) doomed.push(key);
        }
        doomed.forEach(remove);
    } catch {
        // Nothing to do — drafts still expire on their own.
    }
}
